import { watch, type ShallowRef } from 'vue';

import type { Novu, Notification as NovuNotification } from '@novu/js';

import type { TriggerNotificationData } from '@peanuds/shared';

import type { MappableNovuNotification } from '../utils/notifications.mappers';

type SyntheticNotificationMessage = {
  subject: string;
  body: string;
  tags: string[];
};

export type SyntheticNotificationWorkflow = TriggerNotificationData['body']['workflow'] &
  SyntheticNotificationMessage;

export const createSyntheticNovuNotification = (
  workflow: SyntheticNotificationWorkflow,
): MappableNovuNotification => ({
  id: `debug-notification-${Date.now()}`,
  createdAt: new Date().toISOString(),
  isRead: false,
  subject: workflow.subject,
  body: workflow.body,
  tags: [...workflow.tags],
  workflow: {
    identifier: workflow.workflowId,
  },
  data: workflow.payload,
});

/**
 * Manages real-time Novu WebSocket subscriptions.
 *
 * Automatically subscribes whenever a Novu client instance becomes available
 * (including when it is replaced after an organization switch) and cleans up
 * when it disconnects.
 */
export const useNovuEvents = (config: {
  /** Readonly ref to the Novu SDK instance (from useNovuClient). */
  novu: Readonly<ShallowRef<Novu | null>>;
  /** Called each time a Novu client instance becomes ready (initial data load). */
  onReady: () => void;
  /** Called when the Novu client instance is torn down. */
  onDisconnected: () => void;
  /** Called when a new notification arrives via WebSocket. */
  onNotificationReceived: (notification: NovuNotification) => void;
  /** Called when the server signals the unread count changed (external origin only). */
  onUnreadCountChanged: (total: number) => void;
}) => {
  let cleanupReceived: (() => void) | null = null;
  let cleanupUnread: (() => void) | null = null;

  const subscribe = () => {
    const client = config.novu.value;
    if (!client) {
      console.debug('[useNovuEvents] Cannot subscribe — Novu client is null');
      return;
    }

    console.debug('[useNovuEvents] Setting up WebSocket subscriptions...');

    // Defensive: tear down any lingering listeners
    unsubscribe();

    cleanupReceived = client.on('notifications.notification_received', ({ result }) => {
      config.onNotificationReceived(result);
    });

    cleanupUnread = client.on('notifications.unread_count_changed', ({ result }) => {
      config.onUnreadCountChanged(result.total);
    });

    console.debug('[useNovuEvents] ✅ WebSocket subscriptions set up successfully');
  };

  const unsubscribe = () => {
    cleanupReceived?.();
    cleanupReceived = null;
    cleanupUnread?.();
    cleanupUnread = null;
  };

  // Auto-manage lifecycle based on the client instance.
  // `immediate: true` handles the case where the store is created
  // after the Novu client is already initialised (e.g. lazy-loaded route).
  watch(
    config.novu,
    (client) => {
      if (!client) {
        unsubscribe();
        config.onDisconnected();
        return;
      }

      subscribe();
      config.onReady();
    },
    { immediate: true },
  );

  return { subscribe, unsubscribe };
};
